import React, { useState, useEffect } from "react";
import { useApolloClient } from "@apollo/client";
import {
  Card,
  Headline,
  Input,
  InputLabel,
  Flex,
  Button,
  Radio,
} from "_styleguide";
import { Button as BarnButton, message } from "@centrate-io/barn";
import { NumbersOnly } from "_assets/js/helpers";
import { getErrors, baseApi } from "_helpers/api";
import { saveManualAccount } from "_graphql/mutations";
import { validateBankAccount } from "../_helpers/validation";
import {
  IconBuildingBank,
  IconLoader,
  IconCircleCheckFilled,
  IconAlertTriangleFilled,
} from "@tabler/icons-react";
import axios from "axios";
import { useSettings } from "_contexts/useSettings";
import { isDarkColor } from "_helpers/colors";
import "./BankAccount.scss";

function BankAccount(props) {
  const client = useApolloClient();
  const [saving, setSaving] = useState(false);
  const { settings } = useSettings();
  const primaryColor = settings?.primary_color || "#0f766e";
  const primaryFontColor = isDarkColor(primaryColor) ? "#fff" : "#000";

  const [form, setForm] = useState({
    name: undefined,
    account: undefined,
    reAccount: undefined,
    routing: undefined,
    bank: undefined,
    accountType: "checking",
  });

  const [current, setCurrent] = useState({
    loading: false,
    routingDone: false,
    plaidBankName: null,
    plaidBankLogo: null,
    plaidBankUrl: null,
  });

  // Update form by field and value
  const updateForm = (field, value) => {
    const editedform = { ...form };
    editedform[field] = value;
    setForm(editedform);
  };

  // Submit bank account form
  const handleSave = async () => {
    if (!isValid) return;

    setSaving(true);

    const formData = {
      application_id: props.applicationId,
      name: current.plaidBankName || form.bank,
      logo: current.plaidBankLogo || null,
      url: current.plaidBankUrl || null,
      account_number: form.account,
      routing_number: form.routing,
      account_type: form.accountType,
    };

    try {
      await client.mutate({
        variables: formData,
        mutation: saveManualAccount,
      });
      message.success("Bank account saved successfully");
      if (props.onSave) props.onSave();
    } catch (err) {
      message.error(getErrors(err) || "Error saving bank account, try again");
      setSaving(false);
    }
  };

  const handleBack = () => {
    props.setSelectedMethod(undefined);
  };

  useEffect(() => {
    const getInstitution = async (routingNumber) => {
      let institutionData = null;
      try {
        institutionData = await axios.get(
          `${baseApi()}/plaid/routing?routing=${routingNumber}&application_id=${
            props.applicationId
          }`
        );
      } catch (err) {
        institutionData = null;
      }
      return institutionData;
    };

    const doSomething = async () => {
      if (form.routing?.length === 9) {
        setCurrent({ ...current, loading: true });
        const bankData = await getInstitution(form.routing);
        setCurrent({
          loading: false,
          routingDone: true,
          plaidBankName: bankData?.data?.bank,
          plaidBankLogo: bankData?.data?.logo,
          plaidBankUrl: bankData?.data?.url,
        });
      } else if (form.routing?.length < 9 && current?.routingDone) {
        setCurrent({
          loading: false,
          routingDone: false,
          plaidBankName: null,
          plaidBankLogo: null,
          plaidBankUrl: null,
        });
      }
    };
    doSomething();
  }, [form.routing]);

  const validation = validateBankAccount(form, current);
  const isValid = validation?.allValid ? true : false;
  const noMatch =
    validation?.account?.valid &&
    validation.reAccount.started &&
    form?.account?.length <= form?.reAccount?.length &&
    !validation.reAccount.valid;

  return (
    <Card id="payment-information-bank">
      <div className="apply-form">
        <Headline>Bank Account</Headline>
        <div className="pim-bank-account" style={{ marginBottom: "24px" }}>
          <Flex
            className={`routing-box${
              current.routingDone && !current?.plaidBankName ? " no-bank" : ""
            }`}
            vertical={true}
          >
            <InputLabel htmlFor="routing" {...validation.routing}>
              Routing Number *
            </InputLabel>
            <div className="route-input">
              <div
                className={`routing-icon${
                  current?.plaidBankName ? " has-logo" : ""
                }`}
              >
                {current.loading ? (
                  <IconLoader />
                ) : (
                  <>
                    {!current.routingDone || !current.plaidBankLogo ? (
                      <IconBuildingBank />
                    ) : (
                      <img
                        src={`data:image/png;base64, ${current?.plaidBankLogo}`}
                        alt={current?.plaidBankName}
                      />
                    )}
                  </>
                )}
              </div>
              <Input
                id="routing"
                size="medium"
                value={form.routing}
                onChange={(e) =>
                  updateForm("routing", NumbersOnly(e.target.value))
                }
                disabled={current.loading || saving}
                maxLength={9}
                placeholder="e.g. 0493857163..."
                status={
                  validation.routing.started && !validation.routing.valid
                    ? "error"
                    : ""
                }
              />
            </div>
            {current?.plaidBankName ? (
              <div className="has-bank">
                <IconCircleCheckFilled />
                {current?.plaidBankName}
              </div>
            ) : null}
            {current.routingDone && !current?.plaidBankName ? (
              <Flex className="no-bank-found">
                <IconAlertTriangleFilled />
                <div className="nbf-details">
                  <h4>We couldn't verify your routing number</h4>
                  <p>Please enter your bank's name below</p>
                </div>
              </Flex>
            ) : null}
          </Flex>

          {current.routingDone && !current?.plaidBankName ? (
            <Flex vertical={true}>
              <InputLabel htmlFor="bank" {...validation.bank}>
                Enter Your Bank Name
              </InputLabel>
              <Input
                id="bank"
                size="medium"
                value={form.bank}
                onChange={(e) => updateForm("bank", e.target.value)}
                placeholder="e.g. Bank of America"
                disabled={saving}
                status={
                  validation.bank.started && !validation.bank.valid
                    ? "error"
                    : ""
                }
              />
            </Flex>
          ) : null}

          {current.routingDone ? (
            <>
              <Flex vertical={true}>
                <InputLabel htmlFor="account" {...validation.account}>
                  Account Number *
                </InputLabel>
                <Input
                  id="account"
                  size="medium"
                  value={form.account}
                  onChange={(e) =>
                    updateForm("account", NumbersOnly(e.target.value))
                  }
                  placeholder="e.g. 999938472638..."
                  maxLength={16}
                  disabled={saving}
                  status={
                    validation.account.started && !validation.account.valid
                      ? "error"
                      : ""
                  }
                />
              </Flex>
              <Flex vertical={true}>
                <InputLabel htmlFor="reAccount" {...validation.reAccount}>
                  Re-enter Account Number *
                </InputLabel>
                <Input
                  id="reAccount"
                  size="medium"
                  value={form.reAccount}
                  onChange={(e) =>
                    updateForm("reAccount", NumbersOnly(e.target.value))
                  }
                  placeholder="e.g. 999938472638..."
                  maxLength={16}
                  disabled={saving}
                  status={
                    validation.reAccount.started && !validation.reAccount.valid
                      ? "error"
                      : ""
                  }
                />
                {noMatch ? (
                  <div className="no-match">
                    Account numbers must be the same
                  </div>
                ) : null}
              </Flex>
              <Flex vertical={true} className="account-type">
                <InputLabel htmlFor="accountType">Account Type *</InputLabel>
                <Radio.Group
                  style={{
                    "--radio-color": primaryColor,
                    "--radio-font-color": primaryFontColor,
                    "--radio-text-color": settings?.secondary_color || "#000",
                  }}
                  onChange={(e) => updateForm("accountType", e.target.value)}
                  value={form.accountType}
                  disabled={saving}
                >
                  <Radio value="checking">Checking</Radio>
                  <Radio value="savings">Savings</Radio>
                </Radio.Group>
              </Flex>
            </>
          ) : null}
        </div>

        <div className="payment-buttons">
          <Flex gap="12px">
            <BarnButton
              type="secondary"
              size="large"
              onClick={handleBack}
              disabled={saving}
              style={{ flex: 1 }}
            >
              Back
            </BarnButton>
            <Button
              type="primary"
              size="large"
              onClick={handleSave}
              disabled={!isValid}
              loading={saving}
              style={{
                flex: 1,
                background: primaryColor,
                color: primaryFontColor,
              }}
            >
              Save Bank Account
            </Button>
          </Flex>
        </div>
      </div>
    </Card>
  );
}

export default BankAccount;
